<script setup>
import moment from "moment";
import { nextTick } from "vue";
import ArgonBadge from "@/components/ArgonBadge.vue";
import bootstrap from "bootstrap/dist/js/bootstrap.min.js";


const props = defineProps({
  selectedEvidence: {
    type: Object,
    default: null,
  },
  total: {
    type: Number,
    default: 0,
  },
});

function getEvidenceCountLabel(total) {
  const count = Number(total) || 0;
  return `${count} ${count === 1 ? "evidencia" : "evidencias"}`;
}

const vOverflowTooltip = {
  mounted(el, binding) {
    nextTick(() => {
      if (el.scrollWidth > el.clientWidth) {
        el.__tip = new bootstrap.Tooltip(el, {
          title: binding.value,
          placement: "top",
          trigger: "hover",
        });
      }
    });
  },
  updated(el, binding) {
    nextTick(() => {
      if (el.scrollWidth > el.clientWidth) {
        if (el.__tip) {
          el.__tip._config.title = binding.value;
        } else {
          el.__tip = new bootstrap.Tooltip(el, {
            title: binding.value,
            placement: "top",
            trigger: "hover",
          });
        }
      } else if (el.__tip) {
        el.__tip.dispose();
        el.__tip = null;
      }
    });
  },
  unmounted(el) {
    try {
      el.__tip?.dispose();
    } catch {
      /* ignore */
    }
    el.__tip = null;
  },
};

function getTypeLabel(type) {
  const value = String(type || "").trim().toUpperCase();
  if (!value) return "-";
  if (value === "FIRMA") return "Firma";
  if (value === "FOTO") return "Foto";
  return value.charAt(0) + value.slice(1).toLowerCase();
}

function formatDate(date) {
  if (!date) return "-";
  const parsed = moment.utc(date).local();
  if (!parsed.isValid()) return "-";
  return parsed.format("DD/MM/YYYY HH:mm");
}

function getEvidenceName(evidence) {
  const path = String(evidence?.path || "").trim();
  if (!path) return "-";
  const cleanPath = path.split("?")[0];
  const parts = cleanPath.split("/").filter(Boolean);
  return decodeURIComponent(parts[parts.length - 1] || "documento");
}
</script>

<template>
  <div class="card h-100">
    <div v-if="!props.selectedEvidence" class="card-body">
      <div class="evidence-total">
        <span class="evidence-total__value">{{ getEvidenceCountLabel(props.total) }}</span>
      </div>
    </div>

    <div v-else class="card-body">
      <div class="row ">
        <div class="col-4">
          <label class="col-form-label">Tipo</label>
        </div>
        <div class="col-8 d-flex align-items-center">
          <argon-badge color="secondary" size="md" class="evidence-type-badge">
            {{ getTypeLabel(props.selectedEvidence.type) }}
          </argon-badge>
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <label class="col-form-label">Nombre</label>
        </div>
        <div class="col-8">
          <span
            v-overflow-tooltip="getEvidenceName(props.selectedEvidence)"
            class="evidence-detail__ellipsis"
          >
            {{ getEvidenceName(props.selectedEvidence) }}
          </span>
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <label class="col-form-label">Cliente</label>
        </div>
        <div class="col-8">
          <span>{{ props.selectedEvidence.customer || "-" }}</span>
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <label class="col-form-label">Parada</label>
        </div>
        <div class="col-8">
          <span>{{ props.selectedEvidence.stop_number || "-" }}</span>
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <label class="col-form-label">Fecha</label>
        </div>
        <div class="col-8">
          <span>{{ formatDate(props.selectedEvidence.date) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.evidence-total {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.evidence-total__value {
  color: #344767;
  font-size: 0.95rem;
  font-weight: 600;
}

.evidence-type-badge {
  margin-left: 0 !important;
  text-transform: capitalize !important;
}

.evidence-detail__ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
